import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { ArrowRight, Search, Undo2 } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

import { changelog as api } from '@/api/endpoints'
import { Alert, EmptyState, Input, Spinner } from '@/components/ui'
import Pagination from '@/components/ui/Pagination'
import RevertModal from '@/features/changelog/RevertModal'
import { fieldLabelKey, formatValue } from '@/features/changelog/fields'
import { useBootstrap } from '@/hooks/useBootstrap'
import { useAuth } from '@/store/auth'
import { cn, formatDateTime } from '@/lib/utils'
import type { ChangeEntry, ChangeKind, Codebook } from '@/types/api'

/** Poradie záložiek. Kľúč je zároveň `kind` pre API. */
const TABS: Array<{ kind: ChangeKind; labelKey: string }> = [
  { kind: 'client-reservations', labelKey: 'changelog.tabLessons' },
  { kind: 'clients', labelKey: 'changelog.tabClients' },
  { kind: 'instructors', labelKey: 'changelog.tabInstructors' },
  { kind: 'instructor-reservations', labelKey: 'changelog.tabBlocks' },
]

/** Ako sa volá predmet zmeny v danej záložke. */
const SUBJECT_LABEL: Record<ChangeKind, string> = {
  'client-reservations': 'reservation.client',
  clients: 'reservation.client',
  instructors: 'reservation.instructor',
  'instructor-reservations': 'reservation.instructor',
}

/** Záložky, kde má zmysel stĺpec s časom lekcie/bloku. */
const HAS_TIME: ChangeKind[] = ['client-reservations', 'instructor-reservations']

const TYPE_STYLES: Record<string, string> = {
  insert: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
}

/**
 * Prehľad zmien (ClientsPresenter::actionChanges).
 *
 * Audit ukladá celý riadok, teda stav *po* zmene. Pôvodná tabuľka ho tak aj
 * zobrazovala — stĺpce dátum, stav, kurz, jazyk, počet osôb — z čoho sa
 * nedalo zistiť, čo sa vlastne zmenilo. Server teraz posiela rozdiel oproti
 * predchádzajúcemu záznamu a tabuľka ukazuje ten.
 */
export default function ChangelogPage() {
  const { t } = useTranslation()

  const { data: boot } = useBootstrap()
  const [kind, setKind] = useState<ChangeKind>('client-reservations')
  const [query, setQuery] = useState('')
  // Strany sa počítajú od jednotky - `Pagination` aj ľudia to tak berú.
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [revertId, setRevertId] = useState<number | null>(null)
  const [done, setDone] = useState<string | null>(null)
  const readOnly = !useAuth((s) => s.user)?.can_write

  const { data, isLoading } = useQuery({
    queryKey: ['changelog', kind, query, page, pageSize],
    queryFn: () =>
      api.list({
        kind,
        q: query || undefined,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      }),
    placeholderData: keepPreviousData,
  })

  const rows = data?.results ?? []
  const count = data?.count ?? 0
  const pages = Math.max(1, Math.ceil(count / pageSize))

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <h1 className="text-lg font-semibold">{t('changelog.title')}</h1>
        <div className="relative ml-auto">
          <Search size={15} className="absolute left-2.5 top-2.5 text-slate-400" />
          <Input
            value={query}
            onChange={(e) => {
              setQuery(e.target.value)
              setPage(1)
            }}
            placeholder={t('clients.searchPlaceholder')}
            className="w-72 pl-8"
          />
        </div>
      </div>

      <div className="flex flex-wrap gap-1 border-b border-slate-200">
        {TABS.map((tab) => (
          <button
            key={tab.kind}
            type="button"
            onClick={() => {
              setKind(tab.kind)
              setPage(1)
              setDone(null)
            }}
            className={cn(
              'border-b-2 px-3 py-1.5 text-sm',
              tab.kind === kind
                ? 'border-brand-500 font-medium text-brand-700'
                : 'border-transparent text-slate-500 hover:text-slate-800',
            )}
          >
            {t(tab.labelKey)}
          </button>
        ))}
      </div>

      {done ? <Alert variant="success">{done}</Alert> : null}

      <div className="card overflow-x-auto">
        {isLoading ? (
          <div className="flex h-40 items-center justify-center text-slate-400">
            <Spinner className="h-5 w-5" />
          </div>
        ) : rows.length === 0 ? (
          <EmptyState>{t('app.empty')}</EmptyState>
        ) : (
          <table className="table-base">
            <thead>
              <tr>
                <th>{t('changelog.created')}</th>
                <th>{t('changelog.changeType')}</th>
                <th>{t(SUBJECT_LABEL[kind])}</th>
                {HAS_TIME.includes(kind) ? <th>{t('changelog.lesson')}</th> : null}
                {kind === 'client-reservations' ? (
                  <th>{t('reservation.instructor')}</th>
                ) : null}
                <th>{t('changelog.whatChanged')}</th>
                <th>{t('changelog.by')}</th>
                <th className="w-10" />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td className="whitespace-nowrap align-top text-slate-500">
                    {formatDateTime(row.created)}
                  </td>
                  <td className="align-top">
                    <span
                      className={cn(
                        'whitespace-nowrap rounded px-1.5 py-0.5 text-xs font-medium',
                        TYPE_STYLES[row.type ?? ''] ?? 'bg-slate-100 text-slate-700',
                      )}
                    >
                      {t(`changelog.type.${row.type ?? 'update'}`)}
                    </span>
                  </td>
                  <td className="align-top">
                    {row.subject || <span className="text-slate-400">—</span>}
                    <div
                      className="text-xs text-slate-400"
                      title={t('changelog.subjectNumber')}
                    >
                      {t('changelog.number', { id: row.subject_id })}
                    </div>
                  </td>
                  {HAS_TIME.includes(kind) ? (
                    <td className="whitespace-nowrap align-top">
                      {formatDateTime(row.context.start) || '—'}
                    </td>
                  ) : null}
                  {kind === 'client-reservations' ? (
                    <td className="align-top">
                      {row.context.instructor || <span className="text-slate-400">—</span>}
                    </td>
                  ) : null}
                  <td className="align-top">
                    <Changes entry={row} kind={kind} codebooks={boot} />
                  </td>
                  <td className="align-top text-xs text-slate-500">
                    {row.user_name || '—'}
                  </td>
                  <td className="align-top">
                    {!readOnly && row.revertable ? (
                      <button
                        type="button"
                        className="btn-ghost p-1"
                        title={t('changelog.revert')}
                        onClick={() => {
                          setDone(null)
                          setRevertId(row.id)
                        }}
                      >
                        <Undo2 size={14} />
                      </button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}

        <Pagination
          page={page}
          pages={pages}
          pageSize={pageSize}
          count={count}
          onPageChange={setPage}
          onPageSizeChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
        />
      </div>

      {revertId ? (
        <RevertModal
          kind={kind}
          rowId={revertId}
          onClose={() => setRevertId(null)}
          onReverted={(message) => setDone(message)}
        />
      ) : null}
    </div>
  )
}

/** Zoznam zmenených údajov v tvare `pole: staré → nové` - zdieľané aj s
 *  `UndoRedoControl` v kalendári. */
export function Changes({
  entry,
  kind,
  codebooks,
}: {
  entry: ChangeEntry
  /** Druh záznamu - aby vytvorenie / zmazanie povedalo čoho („Rezervácia
   *  inštruktora vytvorená“), nie len všeobecné „Rezervácia vytvorená“. */
  kind?: ChangeKind
  codebooks?: { languages: Codebook[]; courses: Codebook[] }
}) {
  const { t } = useTranslation()
  const ctx = { t, codebooks, formatDateTime }

  if (entry.type === 'delete') {
    return (
      <span className="text-red-700">{t(kind ? `changelog.deletedKind.${kind}` : 'changelog.wasDeleted')}</span>
    )
  }
  // Vytvorenie nemá s čím porovnávať; server ho označí poľom `*`.
  if (entry.changes.some((change) => change.field === '*')) {
    return (
      <span className="text-green-700">{t(kind ? `changelog.createdKind.${kind}` : 'changelog.wasCreated')}</span>
    )
  }
  if (entry.changes.length === 0) {
    return <span className="text-slate-400">{t('changelog.noChange')}</span>
  }

  return (
    <ul className="space-y-0.5">
      {entry.changes.map((change) => (
        <li key={change.field} className="flex flex-wrap items-center gap-1 text-xs">
          <span className="text-slate-500">{t(fieldLabelKey(change.field))}:</span>
          <span className="text-slate-400 line-through">
            {formatValue(change.field, change.old, ctx)}
          </span>
          <ArrowRight size={11} className="shrink-0 text-slate-400" />
          <span className="font-medium">{formatValue(change.field, change.new, ctx)}</span>
        </li>
      ))}
    </ul>
  )
}
